// Piezas de formulario compartidas por los drawers de Administración ·
// {Tiendas y zonas}: el campo con etiqueta, hint y error sobre las clases
// `.field*` de dashboard.css (las mismas que el prototipo), y el mapa de
// códigos de error del servidor a copy es-CL.

import { useId } from "react";
import type { ReactNode } from "react";
import { ApiError } from "../../../lib/api/error";
import type { ActivosWords } from "./words";

export type FieldProps = {
  label: string;
  /** Texto bajo el control (`.field__hint`); va en aria-describedby. */
  hint?: ReactNode;
  /** Error inline (`role="alert"`); reemplaza el hint mientras exista. */
  error?: string | null;
  /** El control recibe `id` y `aria-describedby` ya resueltos. */
  children: (ctl: {
    id: string;
    "aria-describedby": string | undefined;
    "aria-invalid": true | undefined;
  }) => ReactNode;
};

export function Field({ label, hint, error, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const describedBy = error || hint ? hintId : undefined;
  return (
    <div className="field ag-field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {children({ id, "aria-describedby": describedBy, "aria-invalid": error ? true : undefined })}
      {error ? (
        // Misma clase que los errores inline de Equipo (`.auth-error`).
        <span id={hintId} className="auth-error" role="alert">
          {error}
        </span>
      ) : (
        hint && (
          <span id={hintId} className="field__hint">
            {hint}
          </span>
        )
      )}
    </div>
  );
}

// Códigos que las rutas /assets y /asset-groups devuelven hoy → la frase
// que ve el gerente. Lo que no está acá cae al genérico; el mensaje crudo
// del servidor nunca se muestra.
export function saveErrorMessage(err: unknown, w: ActivosWords): string {
  const generic = "No pudimos guardar los cambios. Inténtalo de nuevo en unos minutos.";
  if (!(err instanceof ApiError)) return generic;
  switch (err.code) {
    case "ADMIN_REQUIRED":
    case "ASSET_GROUP_CONFIG_FORBIDDEN":
      return "Tu rol no puede hacer este cambio.";
    case "RECIPE_FORBIDS_ASSET_WITHOUT_GROUP":
      return `Elige la ${w.groupOneLower} de la ${w.assetOneLower}.`;
    case "ASSET_GROUP_NOT_FOUND":
      return `Esa ${w.groupOneLower} ya no existe. Recarga la página.`;
    case "ASSET_NOT_FOUND":
      return `Esa ${w.assetOneLower} ya no existe. Recarga la página.`;
    case "ASSET_GROUP_UPDATE_EMPTY":
      return "No hay cambios que guardar.";
    case "ASSET_GROUP_SUPERVISOR_OCCUPIED":
      return `Esta ${w.groupOneLower} ya tiene responsable. Inténtalo de nuevo.`;
    case "SUPERVISOR_BRAND_MISMATCH":
    case "SUPERVISOR_SPANS_BRANDS":
    case "ASSET_GROUP_SPANS_BRANDS":
      return `La ${w.groupOneLower} y su responsable tienen que ser de la misma ${
        w.brand?.toLowerCase() ?? "marca"
      }.`;
    case "STORE_BRAND_UNRESOLVED":
      return `La ${w.assetOneLower} no tiene ${w.brand?.toLowerCase() ?? "marca"} definida.`;
    case "VALIDATION_ERROR":
      return "Revisa los datos ingresados.";
    default:
      return generic;
  }
}
